import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  TextField,
  Grid,
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from '@mui/icons-material/Search';
import ListItemAgendaReport from './ListItemAgendaReport';
import { fetchReport, handleError, formatDateForMySQL } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import useAgendaActions from '../../hooks/useAgendaActions';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useReportDatePickerLocale } from '../../hooks/useReportDatePickerLocale';

const AgendaReport = () => {
  const { t } = useTranslation();
  const { datePickerFormat, dayjsPickerLocale } = useReportDatePickerLocale();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, state } = useNavigationHelperStore();
  
  // Date filters
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  
  // Usar el hook de acciones de agenda
  const {
    postponeDialog,
    completeDialog,
    handleReschedule,
    handlePostponeClose,
    handlePostponeConfirm,
    handleComplete,
    handleCompleteClose,
    handleCompleteConfirm,
    handleDateChange,
    AgendaDialogs
  } = useAgendaActions(
    // Callback onSuccess para recargar los datos después de completar/posponer
    () => {
      handleSearch(page);
    },
    snackbar
  );

  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { startDate: savedStartDate, endDate: savedEndDate, page: savedPage } = state;
      if (savedStartDate) setStartDate(dayjs(savedStartDate));
      if (savedEndDate) setEndDate(dayjs(savedEndDate));
      if (savedPage) setPage(savedPage);
    }

    handleSearch();
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      page,
    });
  };

  const handleSearch = async (page = 1) => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('AGENDA', page, {
        from: formattedStartDate,
        to: formattedEndDate
      });
      
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = async (event, value) => {
    setPage(value);
    handleSearch(value);
  }

  const handleAgendaEventClick = (agenda) => {
    // Save current state before navigating
    saveCurrentState();
    navigate('/main/view-agenda-event', { state: { id: agenda.id } });
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.from')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format={datePickerFormat}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.to')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format={datePickerFormat}
                minDate={startDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <SearchIcon 
                onClick={() => handleSearch()}
                sx={{ 
                  fontSize: 28, 
                  cursor: 'pointer',
                  color: isLoading ? 'text.disabled' : 'text.primary',
                  display: 'block',
                  margin: 'auto'
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {t('reports.agendaEventsTitle')} ({data.total_results || data.content.length})
                </Typography>
              </Box>
              
              {data.content.map((agenda) => (
                <ListItemAgendaReport
                  key={agenda.id}
                  agenda={agenda}
                  onReschedule={handleReschedule}
                  onComplete={handleComplete}
                  onClick={() => handleAgendaEventClick(agenda)}
                />
              ))}
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noAgendaEvents')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.selectDateRange')}</Typography>
        </Box>
      )}
      
      <AgendaDialogs />
    </Box>
  );
};

export default AgendaReport; 